import errorHandler from '../handlers/ErrorHandler.js';

/**
 * EmailRevealClient - Manages email reveal buttons across the application
 * Handles fetching emails from Cloud Functions and replacing buttons with email links
 * Uses AbortController for proper event listener cleanup
 * 
 * @class EmailRevealClient
 */
class EmailRevealClient {
    constructor(translationFunction) {
        this.t = translationFunction;
        this.listeners = new Map(); // Track button listeners with controllers for cleanup
    }

    /**
     * Setup email reveal button with proper listener management using AbortController
     * @param {string} buttonId - HTML element ID of the button
     * @param {string} cloudFunctionUrl - Full URL to Cloud Function
     * @param {string} section - i18n section for messages (e.g., 'contact', 'termsOfService')
     * @param {string} showLabel - i18n key for initial button text
     * @param {string} loadingLabel - i18n key for loading state text
     */
    setupEmailRevealButton(buttonId, cloudFunctionUrl, section, showLabel = 'showEmail', loadingLabel = 'loadingEmail') {
        requestAnimationFrame(() => {
            const button = document.getElementById(buttonId);
            if (!button) return;

            // Remove old listener if it exists
            if (this.listeners.has(buttonId)) {
                const { controller } = this.listeners.get(buttonId);
                controller.abort();
                this.listeners.delete(buttonId);
            }

            // Set button initial state
            const tFunc = typeof this.t === 'function' ? this.t : (key, def) => def;
            button.textContent = tFunc(`${section}.${showLabel}`, 'Show e-mail');

            // Create AbortController for this listener
            const controller = new AbortController();

            // Create new handler
            const clickHandler = async (event) => {
                event.preventDefault();
                button.textContent = tFunc(`${section}.${loadingLabel}`, 'Loading...');
                button.style.pointerEvents = 'none';
                try {
                    const response = await fetch(cloudFunctionUrl);
                    if (!response.ok) throw new Error(tFunc(`${section}.networkError`, 'Network error'));
                    const data = await response.json();
                    const email = data.email;
                    if (!email) throw new Error(tFunc(`${section}.emailError`, 'Could not load email. Please try again.'));
                    
                    const emailLink = document.createElement('a');
                    emailLink.href = 'mailto:' + email;
                    emailLink.textContent = email;
                    emailLink.style.color = '#2563eb';
                    emailLink.style.textDecoration = 'underline';
                    
                    if (button.parentNode) {
                        button.parentNode.replaceChild(emailLink, button);
                    }
                    
                    // Clean up tracking since button is replaced
                    if (this.listeners.has(buttonId)) {
                        this.listeners.get(buttonId).controller.abort();
                        this.listeners.delete(buttonId);
                    }
                } catch (error) {
                    // Don't show errors if abort was called
                    if (error.name !== 'AbortError') {
                        errorHandler.handle(error, { section, operation: 'emailReveal' }, 'warning');
                        alert(error.message || tFunc(`${section}.emailError`, 'Could not load email. Please try again.'));
                        button.textContent = tFunc(`${section}.${showLabel}`, 'Show e-mail');
                        button.style.pointerEvents = 'auto';
                    }
                }
            };

            // Store handler reference with controller and attach listener
            this.listeners.set(buttonId, { handler: clickHandler, controller });
            button.addEventListener('click', clickHandler, { signal: controller.signal });
        });
    }

    /**
     * Clean up all email reveal button listeners
     * Called before setting up new ones or on view change
     */
    cleanup() {
        this.listeners.forEach(({ controller }, buttonId) => {
            try {
                controller.abort();
            } catch (e) {
                // Ignore abort errors
            }
        });
        this.listeners.clear();
    }

    /**
     * Clear all tracked listeners
     */
    clear() {
        this.cleanup();
    }
}

export default EmailRevealClient;
